import { isNumber, sortBy, times } from 'lodash-es';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';

import { FoodType } from '../../models';
import { foodTypeActions, foodTypeSelectors } from '../../redux';
import { getFoodTypeName } from '../../redux/foodtypes/selectors';
import { Button, Modal, Searchbar, SelectedCheckmark } from '..';
import { Theme } from '../button/Button';
import Ellipsis from '../ellipsis/Ellipsis';

import './SelectFoodItemModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  defaultSelectedFoodType?: FoodType;
  onSelectFoodType: (foodItem: FoodType) => void;
}

const SelectFoodItemModal: FC<Props> = ({ isOpen, closeModal, onSelectFoodType, defaultSelectedFoodType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const allFoodTypes: FoodType[] = useSelector(foodTypeSelectors.getFoodTypes);
  const isLoadingFoodTypes: boolean = useSelector(foodTypeSelectors.isLoading);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredFoodTypes, setFilteredFoodTypes] = useState<FoodType[] | null>(null);
  const [selectedFoodType, setSelectedFoodType] = useState<FoodType | undefined>();

  useEffect(() => {
    dispatch(foodTypeActions.getFoodTypes({ offset: 0, limit: 1000 }));
  }, []);

  const doSearch = () => {
    if (searchTerm.trim().length > 0) {
      setFilteredFoodTypes(
        sortBy(
          allFoodTypes.filter(foodType =>
            getFoodTypeName(foodType)?.toLocaleLowerCase()?.includes(searchTerm.toLocaleLowerCase()),
          ),
          foodType => getFoodTypeName(foodType),
        ),
      );
    } else {
      setFilteredFoodTypes(allFoodTypes);
    }
  };

  useEffect(() => {
    if (allFoodTypes) {
      doSearch();
    }
  }, [allFoodTypes]);

  useEffect(doSearch, [searchTerm]);

  const handleClose = () => {
    setSelectedFoodType(null);
    setSearchTerm('');
    closeModal();
  };

  const handleConfirm = () => {
    onSelectFoodType(selectedFoodType);
    handleClose();
  };

  const handleSelectFoodType = (foodType: FoodType) => {
    if (selectedFoodType === foodType) {
      handleConfirm();
    } else {
      setSelectedFoodType(foodType);
    }
  };

  const renderSearchBar = () => {
    return <Searchbar placeholder={t('phSearch')} value={searchTerm} onChange={setSearchTerm} />;
  };

  return (
    <Modal
      className='select-food-item-modal'
      closeModal={() => closeModal()}
      isOpen={isOpen}
      title={t('titleSelectFoodItem')}
      titleActions={renderSearchBar()}>
      <ul className='food-type-list'>
        {(filteredFoodTypes || times(20)).map(foodType => {
          if (isLoadingFoodTypes || isNumber(foodType)) {
            return <Skeleton key={'food-type-skeleton-' + foodType.id} width={134} height={60} />;
          } else {
            return (
              <button
                key={'food-type-' + foodType.id}
                onClick={() => handleSelectFoodType(foodType)}
                className={(selectedFoodType || defaultSelectedFoodType)?.id === foodType.id ? 'selected' : ''}>
                <Ellipsis text={getFoodTypeName(foodType)} />
                <SelectedCheckmark />
              </button>
            );
          }
        })}
      </ul>

      <div className='buttons'>
        <Button theme={Theme.INVERSE} onClickCallback={handleClose} wide>
          {t('lblCancel')}
        </Button>
        <Button
          theme={Theme.SECONDARY}
          onClickCallback={handleConfirm}
          wide
          isDisabled={!selectedFoodType || selectedFoodType?.id === defaultSelectedFoodType?.id}>
          {t('lblConfirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default SelectFoodItemModal;
